//* Vue
import { SetupContext, ref } from "vue";
//* 组件属性
import type { ModulePickerEmits, ModuleData, ModuleDay } from "../../../src/types";
import { analyzes } from "../../../src/utils";

export const useComposable = (emits: SetupContext<typeof ModulePickerEmits>["emit"]) => {
    //* 响应式变量
    const refs = {
        end: ref<Date>(),
        date: ref<Date>(new Date()),
        days: ref<ModuleDay[]>([]),
        start: ref<Date>(),
    };

    //* 工具函数
    const utils = {
        get_result: (data: ModuleDay) => {
            const result: { start?: Date; end?: Date } = {};
            if ((!refs.start.value && !refs.end.value) || (refs.start.value && refs.end.value)) {
                result.start = new Date(data.date);
            } else if (refs.end.value) {
                result.end = refs.end.value;
                result.start = new Date(data.date);
            } else {
                result.end = new Date(data.date);
                result.start = refs.start.value;
            }

            return analyzes.get_correct_date(result);
        },
    };

    //* 函数列表
    const methods = {
        //* 更新函数
        date: (data: { year?: number; month?: number }) => emits("update", data),
        //* 初始化函数
        init: (data: ModuleData) => {
            //* 数据初始化
            refs.end.value = analyzes.date(data.end);
            refs.start.value = analyzes.date(data.start);
            //* 获取日份列表的开始日期
            methods.update(data.date);
        },

        //* 状态更新
        update: (date: Date) => {
            //* 数据更新
            refs.days.value = [];
            refs.date.value = date;
            //* 获取日份列表的开始日期
            const d = analyzes.get_month_start_date(refs.date.value);
            for (let i = 0; i < 42; i++) {
                const current = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i);
                refs.days.value.push({
                    day: current.getDate(),
                    date: analyzes.string("date", current),
                    year: current.getFullYear(),
                    month: current.getMonth(),
                });
            }
        },

        //* 检测日期状态
        get_day_status: (data: ModuleDay) => {
            //* 数据初始化
            const result: string[] = [];
            //* 检测日期是否为当前月份的日期
            if (refs.date.value?.getMonth() != data.month) {
                result.push("ui-other-status");
            }

            //* 检测日期是否被选中
            const end = refs.end.value;
            const start = refs.start.value;
            const verifyEnd = end?.getDate() == data.day && end?.getFullYear() == data.year && end?.getMonth() == data.month;
            const verifyStart = start?.getDate() == data.day && start?.getFullYear() == data.year && start?.getMonth() == data.month;
            if (verifyEnd || verifyStart) result.push("ui-active");
            else if (start && end) {
                //* 设置时间
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                //* 检测日期是否在范围内
                const current = new Date(data.date).getTime();
                if (current <= end.getTime() && current >= start.getTime()) {
                    result.push("ui-contain-status");
                }
            }

            return result.join(" ");
        },
    };

    //* 事件列表
    const ons = {
        day: (data: ModuleDay) => {
            return {
                //* 数据修改
                click: () => {
                    //* 数据初始化
                    const result = utils.get_result(data);
                    //* 数据更新
                    refs.end.value = result.end;
                    refs.start.value = result.start;
                    //* 事件响应
                    emits("change", result);
                },

                //* 数据预览
                mouseleave: () => {
                    if (refs.start.value && refs.end.value) emits("message", { start: refs.start.value, end: refs.end.value });
                    else {
                        emits("message", { start: undefined, end: undefined });
                    }
                },

                mouseenter: () => {
                    const result = utils.get_result(data);
                    emits("message", {
                        end: result.end,
                        start: result.start,
                    });
                },
            };
        },
    };

    return { ons, refs, methods };
};
